import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ScaleIcon,
  PlayIcon,
  ArrowLeftIcon,
  ClockIcon,
  TableCellsIcon,
  ChevronDownIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import { craApi, hmdaApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import { ModuleIntro } from '../components/ui';

const INCOME_COLORS = { Low: '#dc2626', Moderate: '#d97706', Middle: '#2563eb', Upper: '#16a34a', Unknown: '#6b7280' };

/** True when the HMDA validation's transmittal sheet didn't carry a parseable calendar year. */
function isYearMissing(calendarYear: unknown): boolean {
  if (!calendarYear) return true;
  const n = Number(calendarYear);
  return !Number.isFinite(n) || n < 2018 || n > new Date().getFullYear();
}

function OriginationDistributionChart({ analysis }: { analysis: any }) {
  const low = analysis.lowIncomeTracts ?? 0;
  const moderate = analysis.moderateIncomeTracts ?? 0;
  const middle = analysis.middleIncomeTracts ?? 0;
  const upper = analysis.upperIncomeTracts ?? 0;
  const known = low + moderate + middle + upper;
  // Loans whose census tract had no income classification in our reference
  // for the analysis year. Derive from the in-AA total since the service
  // doesn't yet persist this column.
  const unknown = Math.max(0, (analysis.inAssessmentArea ?? 0) - known);
  const total = known + unknown;
  if (total === 0) return null;

  const data = [
    { level: 'Low', originations: low, pct: (low / total * 100) },
    { level: 'Moderate', originations: moderate, pct: (moderate / total * 100) },
    { level: 'Middle', originations: middle, pct: (middle / total * 100) },
    { level: 'Upper', originations: upper, pct: (upper / total * 100) },
    ...(unknown > 0 ? [{ level: 'Unknown', originations: unknown, pct: (unknown / total * 100) }] : []),
  ];

  return (
    <div className="mt-4 rounded-lg border border-gray-200 p-4">
      <h4 className="text-xs font-semibold text-gray-500 mb-3">Origination Distribution by Tract Income Level</h4>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <XAxis dataKey="level" tick={{ fontSize: 12 }} />
          <YAxis tickFormatter={(v: number) => `${v.toFixed(0)}%`} tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(value: any, name: any) => {
              if (name === 'pct') return [`${Number(value).toFixed(1)}%`, 'Share'];
              return [Number(value).toLocaleString(), 'Originations'];
            }}
          />
          <Legend formatter={() => 'Origination %'} />
          <Bar dataKey="pct" name="pct" radius={[4, 4, 0, 0]}>
            {data.map((entry) => (
              <Cell key={entry.level} fill={INCOME_COLORS[entry.level as keyof typeof INCOME_COLORS]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      {unknown > 0 && (
        <p className="mt-2 text-xs text-gray-500">
          LMI % is computed against {known} originations with classified tract income. {unknown} origination{unknown === 1 ? '' : 's'} had a census tract with no income classification in the reference data and are excluded from the percentage.
        </p>
      )}
    </div>
  );
}

function LendingTrendChart({ analyses }: { analyses: any[] }) {
  // De-duplicate by period — keep only the most recently completed analysis
  // per analysisPeriod so the chart x-axis doesn't repeat labels.
  const byPeriod = new Map<string, any>();
  for (const a of analyses) {
    if (a.status !== 'COMPLETED') continue;
    const key = a.analysisPeriod || String(a.analysisYear);
    const existing = byPeriod.get(key);
    const aTime = new Date(a.completedAt || a.updatedAt || a.createdAt || 0).getTime();
    const eTime = existing ? new Date(existing.completedAt || existing.updatedAt || existing.createdAt || 0).getTime() : -1;
    if (!existing || aTime > eTime) byPeriod.set(key, a);
  }
  const completed = Array.from(byPeriod.values()).sort(
    (a: any, b: any) => (a.analysisYear ?? 0) - (b.analysisYear ?? 0)
  );

  if (completed.length < 2) return null;

  const data = completed.map((a: any) => ({
    year: a.analysisPeriod || String(a.analysisYear),
    lmiPct: a.lmiOriginationPct ?? 0,
    disparity: a.geographicDisparity ?? 0,
  }));

  // Compute deltas between latest and previous
  const latest = completed[completed.length - 1];
  const previous = completed[completed.length - 2];
  const lmiDelta = (latest.lmiOriginationPct ?? 0) - (previous.lmiOriginationPct ?? 0);
  const disparityDelta = (latest.geographicDisparity ?? 0) - (previous.geographicDisparity ?? 0);

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">Year-over-Year Trend</h3>
      <p className="text-xs text-gray-500 mb-4">Comparing {completed.length} completed analyses</p>

      {/* Delta badges */}
      <div className="flex gap-4 mb-4">
        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-gray-500">LMI %:</span>
          <span className={`font-semibold tabular-nums ${lmiDelta > 0 ? 'text-green-600' : lmiDelta < 0 ? 'text-red-600' : 'text-gray-600'}`}>
            {lmiDelta > 0 ? '+' : ''}{lmiDelta.toFixed(1)}pp
          </span>
          {lmiDelta !== 0 && <span className={lmiDelta > 0 ? 'text-green-500' : 'text-red-500'}>{lmiDelta > 0 ? '\u2191' : '\u2193'}</span>}
        </div>
        <div className="flex items-center gap-1.5 text-sm">
          <span className="text-gray-500">Disparity:</span>
          <span className={`font-semibold tabular-nums ${disparityDelta > 0 ? 'text-green-600' : disparityDelta < 0 ? 'text-red-600' : 'text-gray-600'}`}>
            {disparityDelta > 0 ? '+' : ''}{disparityDelta.toFixed(2)}
          </span>
          {disparityDelta !== 0 && <span className={disparityDelta > 0 ? 'text-green-500' : 'text-red-500'}>{disparityDelta > 0 ? '\u2191' : '\u2193'}</span>}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 5, right: 50, left: 10, bottom: 25 }}>
          <XAxis dataKey="year" tick={{ fontSize: 12 }} interval={0} />
          {/* Color each axis to match its line so users can immediately see
              which scale belongs to which metric. */}
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 11, fill: '#2563eb' }}
            tickFormatter={(v: number) => `${v.toFixed(0)}%`}
            label={{ value: 'LMI %', angle: -90, position: 'insideLeft', offset: 10, style: { fill: '#2563eb', fontSize: 11 } }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 11, fill: '#d97706' }}
            domain={[0, 'auto']}
            label={{ value: 'Disparity', angle: 90, position: 'insideRight', offset: 10, style: { fill: '#d97706', fontSize: 11 } }}
          />
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
          <Line yAxisId="left" type="monotone" dataKey="lmiPct" name="LMI Origination %" stroke="#2563eb" strokeWidth={2} dot={{ r: 4 }} />
          <Line yAxisId="right" type="monotone" dataKey="disparity" name="Geographic Disparity" stroke="#d97706" strokeWidth={2} dot={{ r: 4 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function CraLendingAnalysisPage() {
  const { teamId, isLoading: teamLoading } = useTeamId();
  const queryClient = useQueryClient();
  const [selectedValidation, setSelectedValidation] = useState('');
  const [selectedAssessmentAreaId, setSelectedAssessmentAreaId] = useState('');
  const [analysisYearOverride, setAnalysisYearOverride] = useState<string>('');
  const [showTables, setShowTables] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['cra-lending-analyses', teamId],
    queryFn: async () => {
      const res = await craApi.listLendingAnalyses(teamId!);
      return res.data;
    },
    enabled: !!teamId,
    refetchInterval: (query) => {
      const analyses = query.state.data?.analyses;
      const hasRunning = analyses?.some((a: any) => a.status === 'PENDING' || a.status === 'RUNNING');
      return hasRunning ? 5000 : false;
    },
  });

  const { data: validationsData } = useQuery({
    queryKey: ['hmda-validations'],
    queryFn: async () => {
      const res = await hmdaApi.getValidations({ limit: 20 });
      return res.data;
    },
  });

  const { data: aaData } = useQuery({
    queryKey: ['cra-assessment-areas', teamId],
    queryFn: async () => {
      const res = await craApi.listAssessmentAreas(teamId!);
      return res.data;
    },
    enabled: !!teamId,
  });

  const { data: tablesData, isLoading: tablesLoading } = useQuery({
    queryKey: ['cra-aggregate-tables', teamId],
    queryFn: async () => {
      const res = await craApi.getAggregateTables(teamId!);
      return res.data;
    },
    enabled: !!teamId && showTables,
  });

  const runMutation = useMutation({
    mutationFn: (data: any) => craApi.runLendingAnalysis(teamId!, data),
    onSuccess: () => {
      toast.success('Lending analysis started');
      queryClient.invalidateQueries({ queryKey: ['cra-lending-analyses'] });
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.error || err?.message;
      toast.error(detail ? `Failed: ${detail}` : 'Failed to start lending analysis', { duration: 8000 });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (analysisId: string) => craApi.deleteLendingAnalysis(teamId!, analysisId),
    onSuccess: () => {
      toast.success('Analysis deleted');
      queryClient.invalidateQueries({ queryKey: ['cra-lending-analyses'] });
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.error || err?.message;
      toast.error(detail ? `Failed to delete: ${detail}` : 'Failed to delete analysis');
    },
  });

  if (teamLoading || isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-96 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  const analyses = data?.analyses || [];
  const validations = validationsData?.validations || [];
  const assessmentAreas = (aaData?.assessmentAreas || []).filter((a: any) => a.isActive !== false);

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'OUTSTANDING': return 'bg-green-100 text-green-700';
      case 'SATISFACTORY': return 'bg-blue-100 text-blue-700';
      case 'NEEDS_TO_IMPROVE': return 'bg-amber-100 text-amber-700';
      case 'SUBSTANTIAL_NONCOMPLIANCE': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <div>
      <div className="mb-6">
        <Link to="/cra" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to CRA Dashboard
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-navy-900">Lending Analysis</h1>
            <p className="mt-1 text-sm text-gray-500">Analyze origination distribution across income-level census tracts</p>
          </div>
          <button
            onClick={() => setShowTables(!showTables)}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            <TableCellsIcon className="h-4 w-4" />
            {showTables ? 'Hide' : 'View'} Aggregate Tables
          </button>
        </div>
      </div>

      <div className="mb-6">
        <ModuleIntro storageKey="cra-lending-analysis" title="What is Lending Analysis?">
          <p>
            Analyzes where your HMDA originations fall across census tracts by income level
            (low / moderate / middle / upper) and by borrower income — inside vs. outside your delineated
            assessment areas. It's the building block the <span className="font-medium">Lending Test</span> rolls up into
            the multi-year CRA performance-evaluation format.
          </p>
          <ModuleIntro.Points
            items={[
              <>
                <span className="font-medium text-gray-900">Where the data comes from:</span> your HMDA validation data,
                geocoded to census tracts, with assessment-area boundaries from your configured AAs.
              </>,
              <>
                <span className="font-medium text-gray-900">What it produces:</span> lending distribution by geography
                (Table 1) and by borrower income (Table 1b), plus an assessment-area summary (Table 2).
              </>,
              <>
                <span className="font-medium text-gray-900">Analysis History</span> keeps each run so you can compare
                across years and reopen prior results.
              </>,
            ]}
          />
        </ModuleIntro>
      </div>

      {/* Aggregate Tables */}
      {showTables && (
        <div className="space-y-6 mb-6">
          {tablesLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-8 w-48 bg-gray-200 rounded" />
              <div className="h-64 bg-gray-200 rounded-lg" />
            </div>
          ) : tablesData ? (
            <>
              <AggregateLendingTable data={tablesData} />
              <BorrowerIncomeTable data={tablesData} />
              <AssessmentAreaSummaryTable data={tablesData} />
            </>
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              <p className="text-sm text-gray-500">No HMDA data available to generate aggregate tables.</p>
            </div>
          )}
        </div>
      )}

      {/* Run New Analysis */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Run New Lending Analysis</h3>

        {assessmentAreas.length === 0 && (
          <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <p className="font-medium">No assessment areas defined.</p>
            <p className="mt-1">
              Lending analysis requires an AA so loans can be filtered to in-AA originations and disparity can be computed.{' '}
              <Link to="/cra/assessment-areas" className="underline font-medium">Define an assessment area</Link> first.
            </p>
          </div>
        )}

        <div className="flex items-end gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">HMDA Validation</label>
            <select
              value={selectedValidation}
              onChange={e => {
                setSelectedValidation(e.target.value);
                setAnalysisYearOverride(''); // reset override when validation changes
              }}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
            >
              <option value="">Select an HMDA validation...</option>
              {validations.map((v: any) => {
                const yearLabel = isYearMissing(v.calendarYear) ? '⚠ no year detected' : v.calendarYear;
                return (
                  <option key={v.id} value={v.id}>
                    {yearLabel} — {v.totalLoans} loans ({v.status})
                  </option>
                );
              })}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assessment Area <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedAssessmentAreaId}
              onChange={e => setSelectedAssessmentAreaId(e.target.value)}
              disabled={assessmentAreas.length === 0}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Select an assessment area...</option>
              {assessmentAreas.map((a: any) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          {(() => {
            const selected = validations.find((v: any) => v.id === selectedValidation);
            const yearMissing = selected && isYearMissing(selected.calendarYear);
            if (!yearMissing) return null;
            return (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Analysis Year</label>
                <input
                  type="number"
                  min={2018}
                  max={new Date().getFullYear()}
                  value={analysisYearOverride}
                  onChange={(e) => setAnalysisYearOverride(e.target.value)}
                  placeholder="e.g., 2025"
                  className="block w-32 rounded-md border border-amber-400 px-3 py-2 text-sm bg-amber-50 focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                />
                <p className="text-xs text-amber-700 mt-1">Required — your LAR's transmittal sheet didn't include a calendar year.</p>
              </div>
            );
          })()}
          <button
            onClick={() => {
              if (!selectedValidation) {
                toast.error('Please select an HMDA validation');
                return;
              }
              if (!selectedAssessmentAreaId) {
                toast.error('Please select an assessment area — without one, in-AA filtering and geographic disparity cannot be computed.');
                return;
              }
              const selected = validations.find((v: any) => v.id === selectedValidation);
              const yearMissing = selected && isYearMissing(selected.calendarYear);
              const overrideYear = analysisYearOverride ? parseInt(analysisYearOverride, 10) : undefined;
              if (yearMissing && (!overrideYear || isNaN(overrideYear))) {
                toast.error('Please enter the analysis year — your LAR file did not include one.');
                return;
              }
              runMutation.mutate({
                validationId: selectedValidation,
                assessmentAreaId: selectedAssessmentAreaId,
                ...(overrideYear ? { analysisYear: overrideYear } : {}),
              });
            }}
            disabled={runMutation.isPending || !selectedValidation || !selectedAssessmentAreaId}
            className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700 disabled:opacity-50"
          >
            <PlayIcon className="h-4 w-4" />
            {runMutation.isPending ? 'Running...' : 'Run Analysis'}
          </button>
        </div>
      </div>

      {/* Year-over-Year Trend */}
      <LendingTrendChart analyses={analyses} />

      {/* Analysis Results */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Analysis History</h2>
        </div>
        {analyses.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {analyses.map((analysis: any) => (
              <div key={analysis.id} className="px-6 py-5">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium text-gray-900">
                        {analysis.analysisPeriod || analysis.analysisYear}
                      </h3>
                      {analysis.status === 'COMPLETED' && analysis.lendingTestRating && (
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getRatingColor(analysis.lendingTestRating)}`}>
                          {analysis.lendingTestRating.replace(/_/g, ' ')}
                        </span>
                      )}
                      {analysis.status !== 'COMPLETED' && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                          <ClockIcon className="h-3 w-3" />
                          {analysis.status}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Created {new Date(analysis.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      const label = analysis.analysisPeriod || analysis.analysisYear;
                      if (confirm(`Delete analysis "${label}"? This cannot be undone.`)) {
                        deleteMutation.mutate(analysis.id);
                      }
                    }}
                    disabled={deleteMutation.isPending}
                    className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-50"
                    title="Delete this analysis"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                {analysis.status === 'COMPLETED' && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-gray-500">Total Originations</p>
                      <p className="text-lg font-semibold text-navy-900 tabular-nums">{analysis.totalOriginations?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">In Assessment Area</p>
                      <p className="text-lg font-semibold text-navy-900 tabular-nums">{analysis.inAssessmentArea?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">LMI Origination %</p>
                      <p className="text-lg font-semibold text-navy-900 tabular-nums">{analysis.lmiOriginationPct?.toFixed(1)}%</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Geographic Disparity</p>
                      <p className={`text-lg font-semibold tabular-nums ${(analysis.geographicDisparity ?? 1) < 0.8 ? 'text-red-600' : 'text-navy-900'}`}>
                        {analysis.geographicDisparity?.toFixed(2) ?? 'N/A'}
                      </p>
                    </div>
                  </div>
                )}

                {analysis.status === 'COMPLETED' && (() => {
                  const known = (analysis.lowIncomeTracts ?? 0) + (analysis.moderateIncomeTracts ?? 0) +
                    (analysis.middleIncomeTracts ?? 0) + (analysis.upperIncomeTracts ?? 0);
                  const unknown = Math.max(0, (analysis.inAssessmentArea ?? 0) - known);
                  return (
                    <div className={`mt-4 grid gap-2 ${unknown > 0 ? 'grid-cols-5' : 'grid-cols-4'}`}>
                      <div className="rounded-md bg-red-50 p-3 text-center">
                        <p className="text-xs text-gray-500">Low Income</p>
                        <p className="text-sm font-semibold text-red-700 tabular-nums">{analysis.lowIncomeTracts ?? 0}</p>
                      </div>
                      <div className="rounded-md bg-amber-50 p-3 text-center">
                        <p className="text-xs text-gray-500">Moderate</p>
                        <p className="text-sm font-semibold text-amber-700 tabular-nums">{analysis.moderateIncomeTracts ?? 0}</p>
                      </div>
                      <div className="rounded-md bg-blue-50 p-3 text-center">
                        <p className="text-xs text-gray-500">Middle</p>
                        <p className="text-sm font-semibold text-blue-700 tabular-nums">{analysis.middleIncomeTracts ?? 0}</p>
                      </div>
                      <div className="rounded-md bg-green-50 p-3 text-center">
                        <p className="text-xs text-gray-500">Upper</p>
                        <p className="text-sm font-semibold text-green-700 tabular-nums">{analysis.upperIncomeTracts ?? 0}</p>
                      </div>
                      {unknown > 0 && (
                        <div className="rounded-md bg-gray-100 p-3 text-center" title="In-AA originations whose census tract had no income classification in the reference data">
                          <p className="text-xs text-gray-500">Unknown</p>
                          <p className="text-sm font-semibold text-gray-700 tabular-nums">{unknown}</p>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {analysis.status === 'COMPLETED' && (
                  <OriginationDistributionChart analysis={analysis} />
                )}

                {analysis.narrative && (
                  <div className="mt-4 rounded-md bg-gray-50 p-4">
                    <p className="text-xs font-medium text-gray-500 mb-1">AI Narrative</p>
                    <p className="text-sm text-gray-700 whitespace-pre-line">{analysis.narrative}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-16 text-center">
            <ScaleIcon className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-base font-semibold text-navy-900">No Lending Analyses</h3>
            <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
              Select an HMDA validation above and run your first lending analysis to see origination distribution across income-level census tracts.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Table 1: Geographic Distribution ──────────────────────────────────────

function DistributionRow({ row, showAmounts }: { row: any; showAmounts: boolean }) {
  const fmt = (n: number) => n?.toLocaleString() ?? '0';
  const pct = (n: number) => n != null ? n.toFixed(1) + '%' : '—';
  const dollar = (n: number) => n != null ? '$' + Math.round(n).toLocaleString() : '—';

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-2.5 text-sm font-medium text-gray-900 whitespace-nowrap">{row.label}</td>
      <td className="px-3 py-2.5 text-sm text-red-700 tabular-nums text-right">{fmt(row.low.count)}</td>
      <td className="px-3 py-2.5 text-xs text-red-500 tabular-nums text-right">{pct(row.low.pct)}</td>
      <td className="px-3 py-2.5 text-sm text-amber-700 tabular-nums text-right">{fmt(row.moderate.count)}</td>
      <td className="px-3 py-2.5 text-xs text-amber-500 tabular-nums text-right">{pct(row.moderate.pct)}</td>
      <td className="px-3 py-2.5 text-sm text-blue-700 tabular-nums text-right">{fmt(row.middle.count)}</td>
      <td className="px-3 py-2.5 text-xs text-blue-500 tabular-nums text-right">{pct(row.middle.pct)}</td>
      <td className="px-3 py-2.5 text-sm text-green-700 tabular-nums text-right">{fmt(row.upper.count)}</td>
      <td className="px-3 py-2.5 text-xs text-green-500 tabular-nums text-right">{pct(row.upper.pct)}</td>
      <td className="px-3 py-2.5 text-sm font-semibold text-gray-900 tabular-nums text-right">{fmt(row.total.count)}</td>
      {showAmounts && (
        <td className="px-3 py-2.5 text-sm text-gray-600 tabular-nums text-right">{dollar(row.total.amount)}</td>
      )}
    </tr>
  );
}

function AggregateLendingTable({ data }: { data: any }) {
  const [expanded, setExpanded] = useState(true);
  const geo = data.geographicDistribution;
  if (!geo) return null;

  return (
    <div className="bg-white rounded-lg shadow">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 border-b border-gray-200 flex items-center justify-between text-left"
      >
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Table 1 — Lending Distribution by Geography</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Originations by census tract income level &middot; {data.analysisYear} &middot; {data.totalOriginations?.toLocaleString()} total originations
          </p>
        </div>
        <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th colSpan={2} className="px-3 py-2.5 text-center text-xs font-medium text-red-600 uppercase border-l border-gray-200">Low Income</th>
                <th colSpan={2} className="px-3 py-2.5 text-center text-xs font-medium text-amber-600 uppercase border-l border-gray-200">Moderate</th>
                <th colSpan={2} className="px-3 py-2.5 text-center text-xs font-medium text-blue-600 uppercase border-l border-gray-200">Middle</th>
                <th colSpan={2} className="px-3 py-2.5 text-center text-xs font-medium text-green-600 uppercase border-l border-gray-200">Upper</th>
                <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 uppercase border-l border-gray-200">Total</th>
                <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 uppercase border-l border-gray-200">Amount</th>
              </tr>
              <tr className="border-t border-gray-100">
                <th />
                <th className="px-3 py-1 text-right text-[10px] text-gray-400">#</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400">%</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400 border-l border-gray-200">#</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400">%</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400 border-l border-gray-200">#</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400">%</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400 border-l border-gray-200">#</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400">%</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400 border-l border-gray-200">#</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400 border-l border-gray-200">$</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {/* Overall row */}
              <DistributionRow row={geo.overall} showAmounts={true} />

              {/* By Loan Purpose */}
              {geo.byPurpose?.length > 0 && (
                <>
                  <tr>
                    <td colSpan={11} className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      By Loan Purpose
                    </td>
                  </tr>
                  {geo.byPurpose.map((row: any, i: number) => (
                    <DistributionRow key={i} row={row} showAmounts={true} />
                  ))}
                </>
              )}

              {/* By Loan Type */}
              {geo.byType?.length > 0 && (
                <>
                  <tr>
                    <td colSpan={11} className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      By Loan Type
                    </td>
                  </tr>
                  {geo.byType.map((row: any, i: number) => (
                    <DistributionRow key={i} row={row} showAmounts={true} />
                  ))}
                </>
              )}

              {/* Area Demographics comparison */}
              {geo.areaDemographics && (
                <>
                  <tr>
                    <td colSpan={11} className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Assessment Area Demographics (for comparison)
                    </td>
                  </tr>
                  <tr className="bg-blue-50/30">
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-700">Area Tract Distribution</td>
                    <td colSpan={4} className="px-3 py-2.5 text-sm text-gray-600 text-center tabular-nums">
                      {geo.areaDemographics.lmiTracts} LMI tracts ({geo.areaDemographics.lmiPct?.toFixed(1)}%)
                    </td>
                    <td colSpan={4} className="px-3 py-2.5 text-sm text-gray-600 text-center tabular-nums">
                      {geo.areaDemographics.totalTracts - geo.areaDemographics.lmiTracts} non-LMI tracts
                    </td>
                    <td className="px-3 py-2.5 text-sm font-semibold text-gray-900 text-right tabular-nums">
                      {geo.areaDemographics.totalTracts}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-600 text-right tabular-nums">
                      Pop: {geo.areaDemographics.totalPopulation?.toLocaleString()}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Table 1b: Borrower Income Distribution ────────────────────────────────

function BorrowerIncomeTable({ data }: { data: any }) {
  const [expanded, setExpanded] = useState(false);
  const bd = data.borrowerDistribution;
  if (!bd || bd.overall.total.count === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 border-b border-gray-200 flex items-center justify-between text-left"
      >
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Table 1b — Lending Distribution by Borrower Income</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Originations by borrower income relative to Area Median Income (AMI)
          </p>
        </div>
        <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th colSpan={2} className="px-3 py-2.5 text-center text-xs font-medium text-red-600 uppercase border-l border-gray-200">Low (&lt;50%)</th>
                <th colSpan={2} className="px-3 py-2.5 text-center text-xs font-medium text-amber-600 uppercase border-l border-gray-200">Moderate (50-80%)</th>
                <th colSpan={2} className="px-3 py-2.5 text-center text-xs font-medium text-blue-600 uppercase border-l border-gray-200">Middle (80-120%)</th>
                <th colSpan={2} className="px-3 py-2.5 text-center text-xs font-medium text-green-600 uppercase border-l border-gray-200">Upper (&gt;120%)</th>
                <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 uppercase border-l border-gray-200">Total</th>
                <th className="px-3 py-2.5 text-center text-xs font-medium text-gray-500 uppercase border-l border-gray-200">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <DistributionRow row={bd.overall} showAmounts={true} />
              {bd.byPurpose?.map((row: any, i: number) => (
                <DistributionRow key={i} row={row} showAmounts={true} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Table 2: Assessment Area Summary ──────────────────────────────────────

function AssessmentAreaSummaryTable({ data }: { data: any }) {
  const [expanded, setExpanded] = useState(true);
  const areas = data.assessmentAreaSummary;
  if (!areas?.length) return null;

  const dollar = (n: number) => '$' + Math.round(n || 0).toLocaleString();

  return (
    <div className="bg-white rounded-lg shadow">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-6 py-4 border-b border-gray-200 flex items-center justify-between text-left"
      >
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Table 2 — Assessment Area Summary</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Demographics, lending, and community development by assessment area
          </p>
        </div>
        <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase" rowSpan={2}>Assessment Area</th>
                <th colSpan={4} className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l border-gray-200">Demographics</th>
                <th colSpan={4} className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l border-gray-200">Lending</th>
                <th colSpan={2} className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase border-l border-gray-200">CD Activities</th>
              </tr>
              <tr className="border-t border-gray-100">
                <th className="px-3 py-1 text-right text-[10px] text-gray-400 border-l border-gray-200">Tracts</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400">LMI %</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400">Pop</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400">Min %</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400 border-l border-gray-200">Orig</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400">LMI %</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400">Amount</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400">Disparity</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400 border-l border-gray-200">Count</th>
                <th className="px-3 py-1 text-right text-[10px] text-gray-400">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {areas.map((area: any) => (
                <tr key={area.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-sm font-medium text-gray-900 whitespace-nowrap">
                    {area.name}
                    <span className="block text-[10px] text-gray-400">{area.msaCodes?.join(', ')}</span>
                  </td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 tabular-nums text-right border-l border-gray-100">
                    {area.demographics.totalTracts}
                    <span className="text-xs text-gray-400 ml-1">({area.demographics.lmiTracts} LMI)</span>
                  </td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 tabular-nums text-right">{area.demographics.lmiTractPct?.toFixed(1)}%</td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 tabular-nums text-right">{area.demographics.totalPopulation?.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 tabular-nums text-right">{area.demographics.minorityPopulationPct?.toFixed(1)}%</td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 tabular-nums text-right border-l border-gray-100">{area.lending.totalOriginations?.toLocaleString()}</td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 tabular-nums text-right">{area.lending.lmiOriginationPct?.toFixed(1)}%</td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 tabular-nums text-right">{dollar(area.lending.totalAmount)}</td>
                  <td className={`px-3 py-2.5 text-sm font-semibold tabular-nums text-right ${(area.lending.geographicDisparity ?? 1) < 0.8 ? 'text-red-600' : 'text-gray-900'}`}>
                    {area.lending.geographicDisparity?.toFixed(2) ?? '—'}
                  </td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 tabular-nums text-right border-l border-gray-100">{area.cdActivities.totalActivities}</td>
                  <td className="px-3 py-2.5 text-sm text-gray-600 tabular-nums text-right">{dollar(area.cdActivities.totalAmount)}</td>
                </tr>
              ))}
              {/* Totals row */}
              <tr className="bg-gray-50 font-semibold">
                <td className="px-4 py-2.5 text-sm text-gray-900">Total ({areas.length} areas)</td>
                <td className="px-3 py-2.5 text-sm text-gray-900 tabular-nums text-right border-l border-gray-100">
                  {areas.reduce((s: number, a: any) => s + (a.demographics.totalTracts || 0), 0)}
                </td>
                <td className="px-3 py-2.5 text-sm text-gray-900 tabular-nums text-right">
                  {(() => {
                    const total = areas.reduce((s: number, a: any) => s + (a.demographics.totalTracts || 0), 0);
                    const lmi = areas.reduce((s: number, a: any) => s + (a.demographics.lmiTracts || 0), 0);
                    return total > 0 ? (lmi / total * 100).toFixed(1) + '%' : '—';
                  })()}
                </td>
                <td className="px-3 py-2.5 text-sm text-gray-900 tabular-nums text-right">
                  {areas.reduce((s: number, a: any) => s + (a.demographics.totalPopulation || 0), 0).toLocaleString()}
                </td>
                <td className="px-3 py-2.5 text-sm text-gray-900 tabular-nums text-right">—</td>
                <td className="px-3 py-2.5 text-sm text-gray-900 tabular-nums text-right border-l border-gray-100">
                  {areas.reduce((s: number, a: any) => s + (a.lending.totalOriginations || 0), 0).toLocaleString()}
                </td>
                <td className="px-3 py-2.5 text-sm text-gray-900 tabular-nums text-right">—</td>
                <td className="px-3 py-2.5 text-sm text-gray-900 tabular-nums text-right">
                  {dollar(areas.reduce((s: number, a: any) => s + (a.lending.totalAmount || 0), 0))}
                </td>
                <td className="px-3 py-2.5 text-sm text-gray-900 tabular-nums text-right">—</td>
                <td className="px-3 py-2.5 text-sm text-gray-900 tabular-nums text-right border-l border-gray-100">
                  {areas.reduce((s: number, a: any) => s + (a.cdActivities.totalActivities || 0), 0)}
                </td>
                <td className="px-3 py-2.5 text-sm text-gray-900 tabular-nums text-right">
                  {dollar(areas.reduce((s: number, a: any) => s + (a.cdActivities.totalAmount || 0), 0))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
